import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import palette from 'src/theme/palette';
import CloseIcon from '@mui/icons-material/Close';
import {
  Subject as SubjectIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Code as CodeIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import parse from 'html-react-parser';
import useMediaQuery from '@mui/material/useMediaQuery';

const EmailTemplateViewModal = ({ open, onClose, template }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  if (!template) return null;

  /*  Helper: parse body  */
  const parseBodyContent = (body) => {
    if (!body) return null;
    try {
      if (typeof body === 'string') {
        return JSON.parse(body);
      }
      return body;
    } catch {
      return body;
    }
  };

  const bodyContent = parseBodyContent(template.body);

  /*  Date formatting  */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const safeParseHtml = (htmlString) => {
    if (!htmlString || typeof htmlString !== 'string') return htmlString || 'N/A';
    try {
      return parse(htmlString);
    } catch {
      return htmlString;
    }
  };
  /*  Reusable InfoRow  */
  const InfoRow = ({ icon, label, value, color, isHtml = false }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
      <Box sx={{ display: 'flex', minWidth: 180, alignItems: 'center' }}>
        {icon && <Box sx={{ minWidth: 28, display: 'flex', justifyContent: 'center' }}>{icon}</Box>}
        <Typography
          sx={{
            ml: icon ? 1 : 0,
            fontFamily: 'poppinsMedium',
            color: palette.text.secondary,
            fontSize: '14px'
          }}
        >
          {label}:
        </Typography>
      </Box>

      {/* Value column */}
      <Box sx={{ flex: 1 }}>
        {isHtml && typeof value === 'string' && value.includes('<') ? (
          <Box
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: color || palette.text.primary,
              fontSize: '14px',
              lineHeight: 1.5,
              '& p': { margin: 0 },
              '& br': { display: 'block', content: '""', marginTop: '4px' }
            }}
          >
            {safeParseHtml(value)}
          </Box>
        ) : (
          <Typography
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: color || palette.text.primary,
              fontSize: '14px',
              lineHeight: 1.5
            }}
          >
            {value || 'N/A'}
          </Typography>
        )}
      </Box>
    </Box>
  );

  /*  Render body content*/
  const renderBodyContent = () => {
    if (!bodyContent) {
      return (
        <Box
          sx={{
            p: 2.5,
            backgroundColor: palette.grey[50],
            borderRadius: 2,
            border: `1px solid ${palette.grey[200]}`,
            textAlign: 'center',
            color: palette.text.disabled
          }}
        >
          <Typography sx={{ fontFamily: 'poppinsRegular' }}>No content available</Typography>
        </Box>
      );
    }

    /* Template Design  */
    if (Array.isArray(bodyContent)) {
      return (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.text.secondary,
              mb: 2,
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <CodeIcon fontSize="small" /> Template Design
          </Typography>

          {bodyContent.map((record, index) => {
            const entries = Object.entries(record).filter(([key]) => key.toLowerCase() !== 'type');

            return (
              <Card
                key={index}
                variant="outlined"
                sx={{
                  mb: 2,
                  border: `1px solid ${palette.grey[200]}`,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Grid container spacing={2}>
                    {entries.map(([key, value]) => (
                      <Grid item xs={12} md={12} sm={12} key={key}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: { xs: 'flex-start', md: 'flex-start' },
                            flexDirection: { xs: 'column', md: 'row' },
                            mb: 1.5
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'poppinsMedium',
                              color: palette.text.secondary,
                              fontSize: '13px',
                              minWidth: 110,
                              lineHeight: 1.5,
                              pb: { xs: 1, md: 0 },
                              ml: { xs: 1, md: 0 }
                            }}
                          >
                            {key.charAt(0).toUpperCase() + key.slice(1)}:
                          </Typography>

                          <Box sx={{ flex: 1, ml: 1 }}>
                            {typeof value === 'string' && value.includes('<') ? (
                              <Box
                                sx={{
                                  fontFamily: 'poppinsSemiBold',
                                  color: palette.text.primary,
                                  fontSize: '14px',
                                  lineHeight: 1.5,
                                  '& p': { margin: 0 },
                                  '& br': { display: 'block', content: '""', marginTop: '4px' }
                                }}
                              >
                                {safeParseHtml(value)}
                              </Box>
                            ) : (
                              <Typography
                                sx={{
                                  fontFamily: 'poppinsSemiBold',
                                  color: palette.text.primary,
                                  fontSize: '14px',
                                  lineHeight: 1.5
                                }}
                              >
                                {value?.toString() || 'N/A'}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      );
    }

    /* ---------- Single string content ---------- */
    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.text.secondary,
            mb: 2,
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <CodeIcon fontSize="small" /> Template Content
        </Typography>

        <Box
          sx={{
            p: 2.5,
            backgroundColor: palette.grey[50],
            borderRadius: 2,
            border: `1px solid ${palette.grey[200]}`,
            maxHeight: '300px',
            overflow: 'auto'
          }}
        >
          {typeof bodyContent === 'string' && bodyContent.includes('<') ? (
            <Box
              sx={{
                fontFamily: 'poppinsRegular',
                color: palette.text.primary,
                lineHeight: 1.6,
                fontSize: '14px',
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontFamily: 'poppinsSemiBold',
                  margin: '8px 0'
                },
                '& p': { margin: '4px 0' },
                '& br': { display: 'block', content: '""', marginTop: '4px' }
              }}
            >
              {safeParseHtml(bodyContent)}
            </Box>
          ) : (
            <Typography
              sx={{
                fontFamily: 'poppinsRegular',
                color: palette.text.primary,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                fontSize: '14px'
              }}
            >
              {bodyContent}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  /*  Main Dialog  */
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: { xs: 2, sm: 3 },
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : '1400px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2.5,
          position: 'relative',
          borderTopLeftRadius: { xs: 0, sm: 8 },
          borderTopRightRadius: { xs: 0, sm: 8 }
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.secondary.contrastText,
            fontSize: '20px'
          }}
        >
          Email Template Details
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: palette.secondary.contrastText,
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.main,
              mb: 1,
              fontSize: '24px'
            }}
          >
            {template.name}
          </Typography>
        </Box>

        {/* Basic Information */}
        <Box sx={{ mb: 4 }}>
          <Typography
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.text.secondary,
              mb: 2,
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.6
            }}
          >
            <InfoIcon fontSize="small" /> Basic Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoRow
                icon={<InfoIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                label="Template Name"
                value={template?.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoRow
                icon={<CategoryIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                label="Form Type"
                value={template.form_type_name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoRow
                icon={
                  <CheckCircleIcon
                    sx={{
                      color:
                        template.status === 'active' ? palette.success.main : palette.error.main,
                      fontSize: '20px'
                    }}
                  />
                }
                label="Status"
                value={template.status === 'active' ? 'Active' : 'Inactive'}
                color={template.status === 'active' ? palette.success.main : palette.error.main}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />

        {/* Success / Error Messages */}
        {(template.success || template.error) && (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.text.secondary,
                  mb: 2.5,
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.6
                }}
              >
                <CheckCircleIcon fontSize="small" /> Response Messages
              </Typography>

              <Grid container spacing={3}>
                {template.success && (
                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: `1px solid ${palette.success.light}`,
                        borderRadius: 2,
                        backgroundColor: palette.success.light + '10'
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <CheckCircleIcon sx={{ color: palette.success.main, mr: 1.5 }} />
                          <Typography
                            sx={{
                              fontFamily: 'poppinsSemiBold',
                              color: palette.success.main,
                              fontSize: '15px'
                            }}
                          >
                            Success Message
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: 'poppinsRegular',
                            color: palette.text.primary,
                            lineHeight: 1.6,
                            fontSize: '14px'
                          }}
                        >
                          {template.success}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {template.error && (
                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: `1px solid ${palette.error.light}`,
                        borderRadius: 2,
                        backgroundColor: palette.error.light + '10'
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <ErrorIcon sx={{ color: palette.error.main, mr: 1.5 }} />
                          <Typography
                            sx={{
                              fontFamily: 'poppinsSemiBold',
                              color: palette.error.main,
                              fontSize: '15px'
                            }}
                          >
                            Error Message
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: 'poppinsRegular',
                            color: palette.text.primary,
                            lineHeight: 1.6,
                            fontSize: '14px'
                          }}
                        >
                          {template.error}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />
          </>
        )}

        {/* Email Subject */}
        <Box
          sx={{
            mb: 2,
            border: `1px solid ${palette.grey[200]}`,
            borderRadius: 2,
            p: 2.5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <InfoRow
            icon={<SubjectIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
            label="Email Subject"
            value={template.subject}
            isHtml={true}
          />
        </Box>

        {/* Body Content */}
        {renderBodyContent()}

        <Divider sx={{ my: 3, borderColor: palette.grey[300] }} />

        {/* Timestamps */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.text.secondary,
              mb: 2.5,
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.6
            }}
          >
            <CalendarIcon fontSize="small" /> Timestamps
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoRow
                icon={<CalendarIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                label="Created At"
                value={formatDate(template.created_at)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoRow
                icon={<CalendarIcon sx={{ color: palette.primary.main, fontSize: '20px' }} />}
                label="Updated At"
                value={formatDate(template.updated_at)}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.grey[200]}` }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            fontFamily: 'poppinsSemiBold',
            px: 3,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: palette.primary.dark,
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Close Details
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplateViewModal;
